import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response, 
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);
export const takeLead = (leadId) => 
  api.post(`/leads/${leadId}/take`).then(res => res.data);  
export const login = (credentials) => 
  api.post('/login', credentials).then(res => {
    const payload = res.data; 
    
    if (payload.token) {
        localStorage.setItem('token', payload.token);
        localStorage.setItem('user', JSON.stringify(payload.user));
        api.defaults.headers.common['Authorization'] = `Bearer ${payload.token}`;
    }
    return payload; 
  });

export const logout = () => 
  api.post('/logout').finally(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  });

export const getAdminAnalytics = (params) => 
  api.get('/admin/analytics', { params }).then(res => res.data);


export const downloadDashboardExport = async (params) => {
  const { format = 'excel', period = 'week' } = params;
  const searchParams = new URLSearchParams({ format, period });
  const { data } = await api.get(`/admin/analytics/export?${searchParams.toString()}`, {
    responseType: 'blob',
    timeout: 60000,
  });
  const ext = format === 'pdf' ? 'pdf' : 'xlsx';
  const name = `dashboard_${period}_${Date.now()}.${ext}`;
  const url = window.URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const getLeads = (params) => 
  api.get('/leads', { params }).then(res => res.data);


export const downloadLeadsExport = async (params) => {
  const { format = 'excel', ...rest } = params;
  const searchParams = new URLSearchParams({ format, ...rest });
  const response = await api.get(`/leads/export?${searchParams.toString()}`, {
    responseType: 'blob',
    timeout: 120000,
  });
  const contentType = response.headers['content-type'] || '';
  if (response.status >= 400 || contentType.includes('application/json')) {
    const text = await response.data.text();
    let msg = 'Не удалось скачать отчёт';
    try {
      const json = JSON.parse(text);
      msg = json.message || msg;
    } catch (_) {}
    throw Object.assign(new Error(msg), { response: { data: { message: msg } } });
  }
  const ext = format === 'pdf' ? 'pdf' : 'xlsx';
  const name = `leads_${rest.date || 'all'}_${Date.now()}.${ext}`;
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const createLead = (data) => 
  api.post('/leads', data).then(res => res.data);

export const updateLead = (id, data) => 
  api.put(`/leads/${id}`, data).then(res => res.data);

export const completeLeadAction = (id, data) => 
  api.post(`/leads/${id}/complete`, data).then(res => res.data);

export const getLeadMessages = (id) => 
  api.get(`/leads/${id}/messages`).then(res => res.data);

export const sendLeadMessage = (id, text) => 
  api.post(`/leads/${id}/send`, { text }).then(res => res.data);

// --- РАССЫЛКИ ---
export const getMailings = () => 
  api.get('/mailings').then(res => res.data);

export const createMailing = (data) => 
  api.post('/mailings', data).then(res => res.data);

export const clearMailingHistory = () => 
  api.delete('/mailings').then(res => res.data);

// --- ЗАДАЧИ ---
export const getTasks = (params = {}) =>
  api.get('/tasks', { params }).then(res => res.data);

export const createTask = (data) =>
  api.post('/tasks', data).then(res => res.data);

export const startTask = (taskId) =>
  api.post(`/tasks/${taskId}/start`).then(res => res.data);

export const completeTask = (taskId) =>
  api.post(`/tasks/${taskId}/complete`).then(res => res.data);

export const commentTask = (taskId, comment) =>
  api.post(`/tasks/${taskId}/comment`, { comment }).then(res => res.data);

export const deleteTask = (taskId) =>
  api.delete(`/tasks/${taskId}`).then(res => res.data);

// --- ТАЙМЕР И КОМАНДА ---
export const getUsers = () => 
  api.get('/users').then(res => res.data);

export const deleteUser = (id) =>
  api.delete(`/users/${id}`).then(res => res.data);

export const updateManagerGreenApiSlot = (id, greenApiSlot) =>
  api.patch(`/users/${id}/green-api-slot`, {
    green_api_slot: greenApiSlot || null,
  }).then(res => res.data);

export const getAdminTimersToday = (params = {}) =>
  api.get('/admin/timers/today', { params }).then(res => res.data);

export const getAdminManagerMonthlyReport = (managerId, month) =>
  api.get(`/admin/timers/monthly/${managerId}`, { params: { month } }).then(res => res.data);

/**
 * Скачать отчёт по таймеру (Excel/PDF). period: today | week | month | 3months. month: YYYY-MM для month и 3months.
 */
export const downloadTimerReport = async (managerId, { format = 'excel', period = 'today', month = null }) => {
  const params = new URLSearchParams({ format, period });
  if (month) params.set('month', month);
  const { data } = await api.get(`/admin/timers/monthly/${managerId}/export?${params.toString()}`, { responseType: 'blob' });
  const ext = format === 'excel' ? 'xlsx' : 'pdf';
  const name = `timer_report_${managerId}_${period}${month ? '_' + month : ''}.${ext}`;
  const url = window.URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const getMyTimerToday = () =>
  api.get('/timer/today').then(res => res.data);

export const sendTimerAction = (action) =>
  api.post('/timer/action', { action }).then(res => res.data);

// --- УВЕДОМЛЕНИЯ ---
export const getNotifications = (params = {}) =>
  api.get('/notifications', { params }).then(res => res.data);

export const markNotificationRead = (notificationId) =>
  api.post(`/notifications/${notificationId}/read`).then(res => res.data);

export default api;
